import { Cookies } from 'quasar';
import {
  AppAuthTokenKey,
  AppAuthTokenCreatedKey,
  AppAuthRefeshTokenKey,
  AppAuthTokenExpireKey,
  ExpireCookieDays,
  StoreTimezoneKey,
  SelectedStoreIdKey,
  EMPLOYEE_ROUTE_NAME,
} from '@/consts';
import type { Token, IUser } from '@/types/models';
import { useAuthenStore } from '@/stores/authen';
import { useOnboardingStore } from '@/stores/onboarding';
import { useSSRContext } from 'vue';
import { useBase } from '@/composables/base';
import { useLang } from '@/composables/lang';
import { useCache } from '@/composables/cache';
import { addDateByDays } from '@/utils/addDateByDays';
import { authApis } from './apis';
import { canRefreshToken, getTokenStatus } from '@/utils/jwtUtil';
import { useStoreStore } from '@/stores/store-store';
import { useHome } from '@/composables/home';
import { isElectron } from '@/utils/appUtil';
import { getSafeRedirectPath } from '@/utils/redirectUtil';
import type {
  ResendVerificationDtoType,
  SignInDtoType,
} from '@nailsupply/review-swagger/auth';

export const useAuth = () => {
  const ssrContext = process.env.SERVER ? useSSRContext() : null;
  const cookies = process.env.SERVER ? Cookies.parseSSR(ssrContext) : Cookies; // otherwise we're on client
  const authenStore = useAuthenStore();
  const onboardingStore = useOnboardingStore();
  const { appConfirm, appLoading, isDevMode, router } = useBase();
  const { t } = useLang();
  const { logoutClear, saveUserToCache, getUserFromCache, clearUserCache } = useCache();
  const {
    signin: signInApi,
    signinSocial,
    resendVerifyEmail: resendVerifyEmailApi,
  } = authApis();
  const { fetchDataGlobal } = useHome();
  const initAppAuthen = (): void => {
    // Load user from cache if available
    const cachedUser = getUserFromCache();
    if (cachedUser) {
      authenStore.setAuthen(cachedUser as IUser);
    }
  };

  const getAuthCookieByKey = (key: string): string | null => {
    if (!cookies) {
      return null;
    }
    return cookies.get(key) || null;
  };

  const setAuthen = (auth: IUser | null) => {
    if (auth != null) {
      authenStore.setAuthen(auth);
      saveUserToCache(auth);
    }
  };

  const performSignOut = async () => {
    if (!cookies) return;
    appLoading();
    clearUserCache();
    appLoading(false);
    destroyAuthDataAndRedirect();
  };

  const signOut = async () => {
    if (!cookies) return;
    const conf = await appConfirm(t('app.monogram'), t('helper.logoutConfirm'));
    if (conf) await performSignOut();
  };

  const setAuthenticationCookies = (authResponse: Token) => {
    setAuthCookies(cookies, authResponse, !isDevMode());
  };

  const setAuthCookies = (
    cookies: any,
    authResponse: Token,
    secure: boolean = false,
  ) => {
    if (cookies) {
      cookies.set(AppAuthTokenKey, authResponse.accessToken, {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: secure ? AppDomain : null,
        secure,
        sameSite: 'Lax',
      });

      // In Electron, also save to localStorage
      if (isElectron() && typeof window !== 'undefined') {
        localStorage.setItem(AppAuthTokenKey, authResponse.accessToken);
      }

      cookies.set(AppAuthRefeshTokenKey, authResponse.refreshToken, {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: secure ? AppDomain : null,
        secure,
        sameSite: 'Strict',
      });

      // In Electron, also save to localStorage
      if (isElectron() && typeof window !== 'undefined') {
        localStorage.setItem(AppAuthRefeshTokenKey, authResponse.refreshToken);
      }

      // cookies.set(AppAuthTokenExpireKey, authResponse.expiresAt, {
      //   expires: addDateByDays(ExpireCookieDays),
      //   path: '/',
      //   // domain: secure ? AppDomain : null,
      //   secure,
      //   sameSite: 'Strict'
      // });

      cookies.set(AppAuthTokenCreatedKey, Date.now().toString(), {
        expires: addDateByDays(ExpireCookieDays),
        path: '/',
        // domain: secure ? AppDomain : null,
        // secure,
        sameSite: 'Strict',
      });
    }
  };

  const destroyAuthData = () => {
    return new Promise((resolve) => {
      if (cookies) {
        cookies.remove(AppAuthTokenKey, { path: '/' });
        cookies.remove(AppAuthRefeshTokenKey, { path: '/' });
        cookies.remove(AppAuthTokenExpireKey, { path: '/' });
        cookies.remove(AppAuthTokenCreatedKey, { path: '/' });
      }
      // In Electron, also remove from localStorage
      if (isElectron() && typeof window !== 'undefined') {
        localStorage.removeItem(AppAuthTokenKey);
        localStorage.removeItem(AppAuthRefeshTokenKey);
        localStorage.removeItem(AppAuthTokenExpireKey);
        localStorage.removeItem(AppAuthTokenCreatedKey);
      }
      logoutClear();
      resolve(true);
    });
  };

  const destroyAuthDataAndRedirect = async (
    forceRedirectToLoginPage = true,
  ) => {
    await destroyAuthData();
    clearUserCache();
    onboardingStore.resetOnboarding();
    localStorage.removeItem(SelectedStoreIdKey);
    localStorage.removeItem(StoreTimezoneKey);
    authenStore.logout();
    if (forceRedirectToLoginPage) {
      // Use path instead of name to avoid route name resolution issues
      const signinPath = '/auth/signin';

      try {
        // Try using router first
        await router.replace({ path: signinPath });
      } catch {
        // Fallback: use window.location for Electron if router.push fails
        if (isElectron() && typeof window !== 'undefined') {
          window.location.hash = `#${signinPath}`;
        } else {
          // For web, try direct navigation
          window.location.href = signinPath;
        }
      }
    }
  };

  const signInGoogleCode = async (code: string) => {
    if (!code) return null;
    const response = await signinSocial('/api/v1/auth/login-google-code', {
      code,
    });
    await authSuccess(response);
    return response?.data?.user ?? null;
  };

  // Resolve the `?redirect=` param saved by the auth guard into a safe internal
  // path (see redirectUtil for the open-redirect guard).
  const getSafeRedirect = (): string | null =>
    getSafeRedirectPath(router.currentRoute.value?.query?.redirect);

  const authSuccess = async (response: any) => {
    const tokens = response?.data?.tokens ?? response?.data?.token;
    if (tokens) {
      setAuthenticationCookies(tokens);
      // Passcode (staff) login returns `employee` instead of `user`; fall back to it.
      setAuthen(response.data.user ?? response.data.employee);
      await fetchDataGlobal();

      // Prefer the page the user originally tried to reach (saved by the auth
      // guard); fall back to configured homeRoute, then to pick-employees.
      const storeStore = useStoreStore();
      const redirectPath = getSafeRedirect();
      const fallbackTarget = storeStore.homeRoute
        ? storeStore.homeRoute
        : { name: EMPLOYEE_ROUTE_NAME.PICK_EMPLOYEES };
      const homeTarget = redirectPath ?? fallbackTarget;
      await router.push(homeTarget).catch(() => {
        if (isElectron() && typeof window !== 'undefined') {
          window.location.hash = redirectPath
            ? `#${redirectPath}`
            : storeStore.homeRoute
              ? `#${storeStore.homeRoute}`
              : router.resolve({ name: EMPLOYEE_ROUTE_NAME.PICK_EMPLOYEES }).href;
        }
      });
    }
  };

  const signInWithEmail = async (dto: SignInDtoType) => {
    const response = await signInApi(dto);
    await authSuccess(response);
    return response;
  };

  const resendVerifyEmail = async (dto: ResendVerificationDtoType) => {
    return await resendVerifyEmailApi(dto);
  };

  const isUserLoggedIn = () => {
    const cachedUser = getUserFromCache();
    const token = getAuthCookieByKey(AppAuthTokenKey);
    return !!(cachedUser && token);
  };

  const checkLoginStatus = async () => {
    const cachedUser = getUserFromCache();
    const token = getAuthCookieByKey(AppAuthTokenKey);
    if (!cachedUser || !token) {
      return false;
    }
    const tokenStatus = await getTokenStatus(token);
    if (tokenStatus === 'VALID') {
      const needsRefresh = await canRefreshToken(token);
      if (needsRefresh) {
        const refreshResult = await authenStore.refreshToken();
        // Only treat as logged out when the refresh genuinely failed auth
        // (fourceLogout = 401/403). A transient failure leaves the still-valid
        // access token in place, so the session stays valid.
        if (!refreshResult.status && refreshResult.fourceLogout) {
          return false;
        }
      }
      return true;
    }
    else {
      destroyAuthDataAndRedirect();
      return false;
    }
  };

  const getCurrentUser = (): IUser | null => {
    return getUserFromCache() as IUser;
  };


  return {
    initAppAuthen,
    signOut,
    performSignOut,
    setAuthen,
    signInGoogleCode,
    destroyAuthDataAndRedirect,
    setAuthenticationCookies,
    authSuccess,
    signInWithEmail,
    resendVerifyEmail,
    getAuthCookieByKey,
    getUserFromCache,
    clearUserCache,
    isUserLoggedIn,
    getCurrentUser,
    checkLoginStatus,
  };
};
